// Journey event contract: count events by action + stage/outcome/failureCode/route/paymentMethod.
// All dimensions are allowlisted. Never add free text, URL/query strings, medical product IDs,
// addresses, order/payment IDs, search terms, tokens, or gateway error messages here.
const actions = Object.freeze({
  'search': 'search',
  'browse-results': 'browse',
  'sign-in-email': 'auth',
  'sign-in-otp': 'auth',
  'sign-in-google': 'auth',
  'sign-in-facebook': 'auth',
  'sign-up-email': 'auth',
  'sign-up-otp': 'auth',
  'signup-complete': 'auth',
  'otp-request-login': 'auth',
  'otp-request-signup': 'auth',
  'prescription-save': 'prescription',
  'cart-quantity': 'cart',
  'cart-remove': 'cart',
  'wishlist-remove': 'wishlist',
  'order-history': 'order',
  'order-details': 'order',
  'order-reorder': 'order',
  'order-cancel': 'order',
  'order-item-cancel': 'order',
  'order-item-return': 'order',
  'order-track': 'order',
  'add-to-cart': 'cart',
  'buy-now': 'checkout',
  'wishlist-add': 'wishlist',
  'product-view': 'product',
  'checkout-started': 'checkout',
  'address-selected': 'address',
  'payment-required': 'payment',
  'payment-started': 'payment',
  'payment-failed': 'payment',
  'payment-dismissed': 'payment',
  'payment-success': 'payment',
  'order-created': 'order',
  'order-failed': 'order'
});

const outcomes = new Set(['attempted', 'suggested', 'loaded', 'empty', 'selected', 'required', 'started', 'confirmed', 'failed', 'dismissed']);
const failureCodes = new Set([
  'none', 'cart_api_failed', 'product_load_failed', 'address_missing',
  'address_not_deliverable', 'address_validation_failed', 'order_request_failed', 'order_rejected',
  'payment_method_failed', 'payment_sdk_failed', 'payment_gateway_failed',
  'payment_stream_error', 'payment_closed', 'listing_api_failed',
  'auth_provider_failed', 'auth_user_type_rejected', 'otp_request_failed',
  'prescription_save_failed', 'wishlist_api_failed', 'order_api_failed'
]);

export function journeyRoute(pathname) {
  if (typeof pathname !== 'string' || /[?#]/.test(pathname)) return '/other';
  const path = pathname.toLowerCase().replace(/\/$/, '') || '/';
  if (path === '/') return '/';
  if (path === '/checkout') return '/checkout';
  if (path === '/cart') return '/cart';
  if (path === '/wishlist') return '/wishlist';
  if (path === '/products' || path === '/medicines') return path;
  if (path === '/login' || path === '/register' || path === '/user-authdetails-update') return path;
  if (path === '/register/complete') return '/register';
  if (path === '/account' || path.startsWith('/account/')) return '/account';
  if (/^\/order-success\/[^/]+$/.test(path)) return '/order-success';
  if (/^\/[^/]+\/[^/]+\/[^/]+\/medicine$/.test(path)) return '/medicine-detail';
  if (/^\/[^/]+\/[^/]+\/[^/]+$/.test(path)) return '/product-detail';
  if (/^\/[^/]+(?:\/filter)?$/.test(path)) return '/category';
  return '/other';
}

export function journeyAttributes(action, { outcome, failureCode = 'none', paymentMethod, pathname } = {}) {
  if (!Object.hasOwn(actions, action)) return null;
  return {
    stage: actions[action],
    outcome: outcomes.has(outcome) ? outcome : 'attempted',
    failureCode: failureCodes.has(failureCode) ? failureCode : 'none',
    route: journeyRoute(pathname),
    paymentMethod: paymentMethod === 'Cod' ? 'cod'
      : paymentMethod === 'RazorPay Axis' ? 'razorpay_axis'
        : paymentMethod === 'RazorPay Normal' ? 'razorpay_normal'
          : 'unknown'
  };
}

// Scope this tracker to one checkout request or one gateway modal. Keys are fixed stage names,
// not user data; a terminal order result and a gateway outcome must each be counted once.
export function createJourneyOnce(track) {
  const seen = new Set();
  return {
    emit(key, action, attributes) {
      if (seen.has(key)) return false;
      seen.add(key);
      track(action, attributes);
      return true;
    },
    mark(key) { seen.add(key); }
  };
}

// Compare IDs only for telemetry; never add them to event attributes or change checkout handling.
export function matchesCheckoutRequest(data, requestId) {
  return requestId != null && requestId !== '' && data?.requestId === requestId;
}

// Use the order ID observed in Payment_Required to attribute later order events.
export function matchesCheckoutOrder(data, orderId) {
  return orderId != null && orderId !== '' && data?.orderId === orderId;
}
